/**
 * device/apps/atlas.ts — The Atlas (World Map) App
 *
 * A living cartographic world map rendered entirely on Canvas 2D.
 * Rich parchment texture, glowing zone nodes with procedural icons,
 * hover tooltips with zone previews, animated connections, fog-of-war,
 * danger pulses, and a full zone detail panel with travel.
 *
 * Every interaction should feel like tracing a finger across an ancient
 * treasure map spread on a warm tavern table. The map breathes.
 */

import type { GameState } from "../../engine/state.js";
import {
  type DeviceApp,
  type Rect,
  drawRoundRect,
  drawText,
  registerApp,
} from "../app.js";
import {
  type WorldState,
  type WorldZone,
  getZone,
  getConnectedZones,
  canTravel,
  startTravel,
} from "../../world/zones.js";

// ─── App Identifier ──────────────────────────────────────────────────────────

const APP_ID = "atlas";
const APP_NAME = "Atlas";

// ─── Color Palette ────────────────────────────────────────────────────────────

const BG_DEEP         = "#100c06";
const BG_WARM         = "#1e1610";
const BG_PARCHMENT    = "#2a2018";
const C_NODE_UNDISC   = "rgba(80,70,55,0.35)";
const C_CURRENT_GLOW  = "#f0c860";
const C_PATH_SAFE     = "rgba(200,168,120,0.18)";
const C_PATH_DANGER   = "rgba(200,80,60,0.18)";
const C_TEXT_CREAM     = "#f5e6d0";
const C_TEXT_LABEL     = "#c8b898";
const C_TEXT_DIM       = "#6a5a40";
const C_COMPASS_GOLD   = "#c8a050";
const C_DANGER_RED     = "#c05050";
const C_TRAVEL_BTN_A   = "#e8a050";
const C_TRAVEL_BTN_B   = "#c88030";
const C_DETAIL_BG      = "#110e08";
const C_DETAIL_BORDER  = "rgba(200,168,120,0.12)";
const C_TEXT_TAN       = "rgba(220,195,155,0.75)";
const C_PROGRESS_BG    = "rgba(200,168,120,0.1)";
const C_FOG            = "rgba(20,16,10,0.75)";
const C_HOVER_GLOW     = "rgba(240,200,96,0.12)";
const C_TOOLTIP_BG     = "rgba(16,12,6,0.92)";
const C_TOOLTIP_BORDER = "rgba(200,168,120,0.25)";

// ─── Layout ───────────────────────────────────────────────────────────────────

const NODE_R          = 12;       // base discovered node radius
const NODE_R_CURRENT  = 15;       // current zone node radius
const NODE_R_UNDISC   = 9;        // undiscovered node radius
const HOVER_INFLATE   = 3;        // extra radius on hover
const MAP_PAD_TOP     = 6;        // top padding for map area
const MAP_PAD_BOTTOM  = 32;       // bottom padding (room for return-home)
const MAP_PAD_X       = 8;        // horizontal padding
const SCROLL_FRICTION = 6;
const DETAIL_W_FRAC   = 0.92;

// Aliases for backward compat within this file
const C_BG_MID = BG_WARM;
const C_BG_DARK = BG_DEEP;
const C_MAP_BORDER = "rgba(200,168,120,0.2)";
const NODE_RADIUS = NODE_R;
const NODE_RADIUS_BIG = NODE_R_CURRENT;
const LEGEND_ENTRY_H = 11;
const DETAIL_PANEL_W_FRAC = DETAIL_W_FRAC;
const hexWithAlpha = hexAlpha;

// ─── Per-App State ────────────────────────────────────────────────────────────

interface AtlasState {
  selectedZoneId: string | null;
  mapOffsetX: number;
  mapOffsetY: number;
  mapZoom: number;
  scrollY: number;
  scrollVelocity: number;
  maxScrollY: number;
  viewMode: "map" | "detail";
  elapsed: number;
  detailRect: Rect | null;
  nodePositions: Record<string, { x: number; y: number }>;
  travelBtnRect: Rect | null;
  backBtnRect: Rect | null;
  connectionRects: Array<{ zoneId: string; rect: Rect }>;
  returnHomeRect: Rect | null;
  /** Zone ID the pointer is currently hovering over (map view) */
  hoveredZoneId: string | null;
  /** Pointer position in content-local coords (-1 if outside) */
  pointerX: number;
  pointerY: number;
}

function getAppState(state: GameState): AtlasState {
  if (!state.player.device.appStates[APP_ID]) {
    state.player.device.appStates[APP_ID] = {
      selectedZoneId: null,
      mapOffsetX: 0,
      mapOffsetY: 0,
      mapZoom: 1.0,
      scrollY: 0,
      scrollVelocity: 0,
      maxScrollY: 0,
      viewMode: "map",
      elapsed: 0,
      detailRect: null,
      nodePositions: {},
      travelBtnRect: null,
      backBtnRect: null,
      connectionRects: [],
      returnHomeRect: null,
      hoveredZoneId: null,
      pointerX: -1,
      pointerY: -1,
    } satisfies AtlasState;
  }
  return state.player.device.appStates[APP_ID] as AtlasState;
}

// ─── World State Accessor ─────────────────────────────────────────────────────

function getWorldState(state: GameState): WorldState | null {
  return state.world ?? null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace("#", "");
  if (clean.length === 3) {
    return {
      r: parseInt(clean[0] + clean[0], 16),
      g: parseInt(clean[1] + clean[1], 16),
      b: parseInt(clean[2] + clean[2], 16),
    };
  }
  return {
    r: parseInt(clean.substring(0, 2), 16),
    g: parseInt(clean.substring(2, 4), 16),
    b: parseInt(clean.substring(4, 6), 16),
  };
}

function hexAlpha(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  font: string,
): string[] {
  ctx.font = font;
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines.length > 0 ? lines : [""];
}

function dangerColor(level: number): string {
  if (level === 0) return "#60c060";
  if (level === 1) return "#d0c040";
  if (level === 2) return "#e08040";
  return "#c03030";
}

function typeLabel(type: WorldZone["type"]): string {
  const map: Record<WorldZone["type"], string> = {
    residential: "Residential", commercial: "Commercial", nature: "Nature",
    harbor: "Harbor", mountain: "Mountain", underground: "Underground",
    skyline: "Skyline", forest: "Forest", crafting: "Crafting",
    social: "Social", danger: "Danger", mystery: "Mystery", training: "Training",
  };
  return map[type] ?? type;
}

function typeBadgeColor(type: WorldZone["type"]): string {
  const map: Record<WorldZone["type"], string> = {
    residential: "#c8a050", commercial: "#e88040", nature: "#60a060",
    harbor: "#5080a0", mountain: "#8a7a6a", underground: "#6a50a0",
    skyline: "#70a0c0", forest: "#408050", crafting: "#a07040",
    social: "#e8c070", danger: "#c05050", mystery: "#9060b0", training: "#d4a030",
  };
  return map[type] ?? "#888888";
}

function weatherLabel(w: WorldZone["defaultWeather"]): string {
  const m: Record<WorldZone["defaultWeather"], string> = {
    clear: "Clear", rain: "Rainy", snow: "Snowy",
    fog: "Foggy", wind: "Windy", storm: "Stormy",
  };
  return m[w];
}

/** Zone icon glyph — small symbol drawn inside the node circle */
function zoneIconGlyph(icon: string): string {
  const map: Record<string, string> = {
    home: "⌂", fountain: "◈", lantern: "◎", leaf: "❧",
    anchor: "⚓", anvil: "⚒", tree: "♣", peak: "▲",
    crystal: "◆", cloud: "☁", gem: "♦", storm: "⚡", sword: "⚔",
  };
  return map[icon] ?? "•";
}

// ─── Map Drawing ──────────────────────────────────────────────────────────────

function drawMapBackground(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
): void {
  // Aged parchment gradient
  const bg = ctx.createLinearGradient(rect.x, rect.y, rect.x + rect.w, rect.y + rect.h);
  bg.addColorStop(0, BG_WARM);
  bg.addColorStop(0.5, BG_PARCHMENT);
  bg.addColorStop(1, BG_DEEP);
  ctx.fillStyle = bg;
  ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

  // Grain texture — subtle horizontal lines for parchment feel
  ctx.save();
  const seed = 42;
  const rng = (n: number) => (Math.sin(n * seed * 9301 + 49297) * 0.5 + 0.5);
  for (let i = 0; i < 80; i++) {
    const lineY = rect.y + rng(i) * rect.h;
    const lineAlpha = rng(i + 100) * 0.035;
    ctx.beginPath();
    ctx.moveTo(rect.x, lineY);
    ctx.lineTo(rect.x + rect.w, lineY);
    ctx.strokeStyle = `rgba(200,180,140,${lineAlpha})`;
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  // Vignette — darker corners for depth
  const vig = ctx.createRadialGradient(
    rect.x + rect.w / 2, rect.y + rect.h / 2, rect.w * 0.2,
    rect.x + rect.w / 2, rect.y + rect.h / 2, rect.w * 0.75,
  );
  vig.addColorStop(0, "rgba(0,0,0,0)");
  vig.addColorStop(1, "rgba(0,0,0,0.45)");
  ctx.fillStyle = vig;
  ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

  // Ornamental border — double-line cartographic frame
  ctx.strokeStyle = "rgba(200,168,120,0.12)";
  ctx.lineWidth = 1;
  ctx.strokeRect(rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4);
  ctx.strokeStyle = "rgba(200,168,120,0.08)";
  ctx.lineWidth = 0.5;
  ctx.strokeRect(rect.x + 4, rect.y + 4, rect.w - 8, rect.h - 8);

  ctx.restore();
}

function drawConnectionPaths(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  zones: WorldZone[],
  nodePositions: Record<string, { x: number; y: number }>,
): void {
  const drawn = new Set<string>();

  for (const zone of zones) {
    for (const connId of zone.connections) {
      const pairKey = [zone.id, connId].sort().join(":");
      if (drawn.has(pairKey)) continue;
      drawn.add(pairKey);

      const conn = zones.find(z => z.id === connId);
      if (!conn) continue;

      // Only draw if at least one endpoint is discovered
      if (zone.explored === 0 && conn.explored === 0) continue;

      const aPos = nodePositions[zone.id];
      const bPos = nodePositions[connId];
      if (!aPos || !bPos) continue;

      const isDangerous =
        zone.dangerLevel >= 2 || conn.dangerLevel >= 2;

      // Draw with a subtle quadratic curve for organic feel
      const mx = (aPos.x + bPos.x) / 2;
      const my = (aPos.y + bPos.y) / 2;
      const dx = bPos.x - aPos.x;
      const dy = bPos.y - aPos.y;
      const perpX = -dy * 0.08;
      const perpY = dx * 0.08;

      ctx.save();
      ctx.setLineDash([3, 5]);
      ctx.lineDashOffset = 0;
      ctx.strokeStyle = isDangerous ? C_PATH_DANGER : C_PATH_SAFE;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(aPos.x, aPos.y);
      ctx.quadraticCurveTo(mx + perpX, my + perpY, bPos.x, bPos.y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
  }

  void rect; // rect used for node position calculation upstream
}

function drawZoneNode(
  ctx: CanvasRenderingContext2D,
  zone: WorldZone,
  nx: number,
  ny: number,
  isCurrent: boolean,
  isHovered: boolean,
  elapsed: number,
): void {
  const discovered = zone.explored > 0;
  const baseR = isCurrent ? NODE_R_CURRENT : (discovered ? NODE_R : NODE_R_UNDISC);
  const radius = baseR + (isHovered ? HOVER_INFLATE : 0);

  ctx.save();

  if (!discovered) {
    // Fog of war — soft overlapping smoke clouds
    ctx.globalAlpha = 0.5 + Math.sin(elapsed * 0.8 + nx * 0.1) * 0.08;
    for (let i = 0; i < 3; i++) {
      const offX = (i - 1) * 5;
      const offY = i === 1 ? -3 : 1.5;
      ctx.beginPath();
      ctx.arc(nx + offX, ny + offY, radius + 4, 0, Math.PI * 2);
      ctx.fillStyle = C_FOG;
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Undiscovered node body — dark muted circle
    ctx.beginPath();
    ctx.arc(nx, ny, radius, 0, Math.PI * 2);
    ctx.fillStyle = C_NODE_UNDISC;
    ctx.fill();
    ctx.strokeStyle = "rgba(80,70,55,0.35)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Dimmed zone-appropriate icon inside (hints at what's there)
    const glyph = zoneIconGlyph(zone.icon);
    ctx.font = `${Math.round(radius * 0.8)}px sans-serif`;
    ctx.fillStyle = "rgba(120,105,80,0.4)";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(glyph, nx, ny + 0.5);

    // Greyed-out first few chars of name + "..."
    const hint = zone.name.length > 5
      ? zone.name.slice(0, 4) + "…"
      : zone.name;
    ctx.font = "italic 6px sans-serif";
    ctx.fillStyle = "rgba(150,135,110,0.5)";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(hint, nx, ny + radius + 3);

    ctx.restore();
    return;
  }

  // ── Hover glow halo ────────────────────────────────────────────────
  if (isHovered) {
    const haloR = radius + 6;
    const haloGrad = ctx.createRadialGradient(nx, ny, radius, nx, ny, haloR);
    haloGrad.addColorStop(0, hexAlpha(zone.primaryColor, 0.25));
    haloGrad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.beginPath();
    ctx.arc(nx, ny, haloR, 0, Math.PI * 2);
    ctx.fillStyle = haloGrad;
    ctx.fill();
  }

  // ── Danger pulse ───────────────────────────────────────────────────
  if (zone.dangerLevel >= 2) {
    const pulsePhase = elapsed * 2.2 + nx * 0.05;
    const pulseR = radius + 6 + Math.sin(pulsePhase) * 3;
    const pulseAlpha = 0.06 + Math.sin(pulsePhase) * 0.04;
    ctx.beginPath();
    ctx.arc(nx, ny, pulseR, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(200,60,60,${pulseAlpha})`;
    ctx.fill();
  }

  // ── Current zone — golden breathing ring + radial glow ─────────────
  if (isCurrent) {
    const breathe = Math.sin(elapsed * 2.5);
    const glowR = radius + 7 + breathe * 2;
    const glowAlpha = 0.20 + breathe * 0.10;

    // Radial golden glow
    const currentGrad = ctx.createRadialGradient(nx, ny, radius * 0.5, nx, ny, glowR);
    currentGrad.addColorStop(0, `rgba(240,200,96,${(glowAlpha * 0.4).toFixed(3)})`);
    currentGrad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.beginPath();
    ctx.arc(nx, ny, glowR, 0, Math.PI * 2);
    ctx.fillStyle = currentGrad;
    ctx.fill();

    // Animated ring
    ctx.beginPath();
    ctx.arc(nx, ny, radius + 3 + breathe, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(240,200,96,${glowAlpha.toFixed(3)})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // ── Node body — gradient fill ──────────────────────────────────────
  const nodeGrad = ctx.createRadialGradient(
    nx - radius * 0.2, ny - radius * 0.3, 0,
    nx, ny, radius,
  );
  nodeGrad.addColorStop(0, zone.secondaryColor);
  nodeGrad.addColorStop(1, zone.primaryColor);
  ctx.beginPath();
  ctx.arc(nx, ny, radius, 0, Math.PI * 2);
  ctx.fillStyle = nodeGrad;
  ctx.fill();

  // Inner highlight — subtle specular
  ctx.beginPath();
  ctx.arc(nx - radius * 0.15, ny - radius * 0.25, radius * 0.45, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.fill();

  // Border
  ctx.beginPath();
  ctx.arc(nx, ny, radius, 0, Math.PI * 2);
  ctx.strokeStyle = hexAlpha(zone.primaryColor, isCurrent ? 1.0 : 0.7);
  ctx.lineWidth = isCurrent ? 2 : 1.2;
  ctx.stroke();

  // ── Icon glyph inside the node ─────────────────────────────────────
  const glyph = zoneIconGlyph(zone.icon);
  const glyphSize = Math.round(radius * 0.85);
  ctx.font = `${glyphSize}px sans-serif`;
  ctx.fillStyle = isCurrent ? "#1a1008" : "rgba(255,255,255,0.85)";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(glyph, nx, ny + 0.5);

  // ── Current zone marker — pulsing gold diamond ─────────────────────
  if (isCurrent) {
    ctx.save();
    ctx.translate(nx, ny - radius - 5);
    ctx.rotate(Math.PI / 4);
    const diamondSize = 3;
    ctx.fillStyle = C_CURRENT_GLOW;
    ctx.fillRect(-diamondSize, -diamondSize, diamondSize * 2, diamondSize * 2);
    ctx.restore();
  }

  // ── Waystone star (discovered but not current) ─────────────────────
  if (zone.waystoneDiscovered && !isCurrent) {
    drawStar(ctx, nx + radius - 2, ny - radius + 2, 3.5, C_CURRENT_GLOW);
  }

  // ── Danger exclamation ─────────────────────────────────────────────
  if (zone.dangerLevel === 3) {
    ctx.font = "bold 8px sans-serif";
    ctx.fillStyle = C_DANGER_RED;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("!", nx + radius + 2, ny - radius - 2);
  }

  // ── Zone name label below node ──────────────────────────────────────
  const labelFont = isCurrent ? "bold 7px sans-serif" : "6px sans-serif";
  const labelColor = isCurrent ? C_TEXT_CREAM : C_TEXT_LABEL;
  ctx.font = labelFont;
  ctx.fillStyle = labelColor;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(zone.name, nx, ny + radius + 3);

  ctx.restore();
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  color: string,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const outerAngle = (i * 2 * Math.PI) / 5 - Math.PI / 2;
    const innerAngle = outerAngle + Math.PI / 5;
    if (i === 0) {
      ctx.moveTo(cx + Math.cos(outerAngle) * r, cy + Math.sin(outerAngle) * r);
    } else {
      ctx.lineTo(cx + Math.cos(outerAngle) * r, cy + Math.sin(outerAngle) * r);
    }
    ctx.lineTo(
      cx + Math.cos(innerAngle) * (r * 0.45),
      cy + Math.sin(innerAngle) * (r * 0.45),
    );
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawCompassRose(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
): void {
  const r = 14;
  ctx.save();

  // Background circle — semi-transparent parchment
  ctx.beginPath();
  ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(30,24,14,0.6)";
  ctx.fill();
  ctx.strokeStyle = "rgba(200,160,80,0.2)";
  ctx.lineWidth = 0.8;
  ctx.stroke();

  const dirs = [
    { label: "N", angle: -Math.PI / 2, thick: true },
    { label: "S", angle:  Math.PI / 2, thick: false },
    { label: "E", angle:  0,           thick: false },
    { label: "W", angle:  Math.PI,     thick: false },
  ];

  for (const d of dirs) {
    const ex = cx + Math.cos(d.angle) * r;
    const ey = cy + Math.sin(d.angle) * r;

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(ex, ey);
    ctx.strokeStyle = C_COMPASS_GOLD;
    ctx.lineWidth = d.thick ? 1.8 : 1.0;
    ctx.stroke();

    // Label
    const lx = cx + Math.cos(d.angle) * (r + 7);
    const ly = cy + Math.sin(d.angle) * (r + 7);
    ctx.font = `${d.thick ? "bold " : ""}7px sans-serif`;
    ctx.fillStyle = C_COMPASS_GOLD;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(d.label, lx, ly);
  }

  // Center dot
  ctx.beginPath();
  ctx.arc(cx, cy, 2, 0, Math.PI * 2);
  ctx.fillStyle = C_COMPASS_GOLD;
  ctx.fill();

  ctx.restore();
}

// ─── Hover Tooltip ────────────────────────────────────────────────────────────

function drawHoverTooltip(
  ctx: CanvasRenderingContext2D,
  zone: WorldZone,
  nx: number,
  ny: number,
  mapRect: Rect,
  isCurrent: boolean,
): void {
  const TT_W = 110;
  const TT_PAD = 6;
  const lineH = 10;

  // Name + type + danger + "You are here" / "Tap to view"
  const lines: Array<{ text: string; font: string; color: string }> = [];
  lines.push({ text: zone.name, font: "bold 8px sans-serif", color: C_TEXT_CREAM });

  const tl = typeLabel(zone.type);
  const dc = dangerColor(zone.dangerLevel);
  const dangerDots = zone.dangerLevel > 0
    ? " " + "\u25CF".repeat(zone.dangerLevel) + "\u25CB".repeat(3 - zone.dangerLevel)
    : "";
  lines.push({ text: tl + dangerDots, font: "7px sans-serif", color: dc });

  if (isCurrent) {
    lines.push({ text: "You are here", font: "italic 7px sans-serif", color: C_CURRENT_GLOW });
  } else {
    lines.push({ text: "Tap to view", font: "italic 7px sans-serif", color: C_TEXT_DIM });
  }

  const TT_H = TT_PAD * 2 + lines.length * lineH;

  // Position: prefer below the node, flip above if too close to bottom
  let tx = nx - TT_W / 2;
  let ty = ny + NODE_R_CURRENT + 8;
  if (ty + TT_H > mapRect.y + mapRect.h - 4) {
    ty = ny - NODE_R_CURRENT - TT_H - 4;
  }
  // Clamp horizontal
  tx = Math.max(mapRect.x + 2, Math.min(mapRect.x + mapRect.w - TT_W - 2, tx));

  ctx.save();

  // Background
  drawRoundRect(ctx, tx, ty, TT_W, TT_H, 5, C_TOOLTIP_BG, C_TOOLTIP_BORDER);

  // Color accent bar at top
  ctx.fillStyle = zone.primaryColor;
  ctx.fillRect(tx + 4, ty + 2, TT_W - 8, 1.5);

  // Text
  let textY = ty + TT_PAD + 2;
  for (const line of lines) {
    ctx.font = line.font;
    ctx.fillStyle = line.color;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(line.text, tx + TT_W / 2, textY);
    textY += lineH;
  }

  ctx.restore();
}

function drawUndiscoveredTooltip(
  ctx: CanvasRenderingContext2D,
  zone: WorldZone,
  nx: number,
  ny: number,
  mapRect: Rect,
): void {
  const TT_W = 100;
  const TT_PAD = 5;
  const lineH = 10;

  // Truncated name hint
  const nameHint = zone.name.length > 8
    ? zone.name.slice(0, 6) + "…"
    : zone.name;

  const lines: Array<{ text: string; font: string; color: string }> = [
    { text: nameHint, font: "italic 7px sans-serif", color: "rgba(160,145,120,0.7)" },
    { text: "Unexplored", font: "bold 7px sans-serif", color: C_TEXT_DIM },
    { text: "Venture forth…", font: "italic 6px sans-serif", color: "rgba(180,160,120,0.45)" },
  ];

  const TT_H = TT_PAD * 2 + lines.length * lineH;

  let tx = nx - TT_W / 2;
  let ty = ny + NODE_R_UNDISC + 16;
  if (ty + TT_H > mapRect.y + mapRect.h - 4) {
    ty = ny - NODE_R_UNDISC - TT_H - 4;
  }
  tx = Math.max(mapRect.x + 2, Math.min(mapRect.x + mapRect.w - TT_W - 2, tx));

  ctx.save();

  // Darker, more muted tooltip background
  drawRoundRect(ctx, tx, ty, TT_W, TT_H, 4, "rgba(25,20,12,0.85)", "rgba(100,90,70,0.25)");

  let textY = ty + TT_PAD + 1;
  for (const line of lines) {
    ctx.font = line.font;
    ctx.fillStyle = line.color;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(line.text, tx + TT_W / 2, textY);
    textY += lineH;
  }

  ctx.restore();
}

function drawLegend(
  ctx: CanvasRenderingContext2D,
  rx: number,
  ry: number,
): void {
  const entries = [
    { color: "#c8a050", label: "Residential" },
    { color: "#5080a0", label: "Harbor" },
    { color: "#408050", label: "Forest" },
    { color: "#6a50a0", label: "Underground" },
    { color: "#c05050", label: "Danger" },
  ];

  const padX = 6;
  const padY = 5;
  const w = 72;
  const h = padY * 2 + entries.length * LEGEND_ENTRY_H;

  // Background
  drawRoundRect(
    ctx, rx, ry, w, h, 4,
    "rgba(20,16,10,0.75)",
    "rgba(200,168,120,0.1)",
  );

  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    const ey = ry + padY + i * LEGEND_ENTRY_H + LEGEND_ENTRY_H / 2;

    // Dot
    ctx.beginPath();
    ctx.arc(rx + padX + 4, ey, 3, 0, Math.PI * 2);
    ctx.fillStyle = e.color;
    ctx.fill();

    // Label
    ctx.font = "6px sans-serif";
    ctx.fillStyle = C_TEXT_LABEL;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(e.label, rx + padX + 11, ey);
  }
}

// ─── Detail Panel Drawing ─────────────────────────────────────────────────────

function drawDetailPanel(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  zone: WorldZone,
  appState: AtlasState,
  worldState: WorldState,
  state: GameState,
  elapsed: number,
): void {
  const pw = rect.w * DETAIL_PANEL_W_FRAC;
  const ph = rect.h;
  const px = rect.x + (rect.w - pw) / 2;
  const py = rect.y;

  // Store detail rect for hit-testing
  appState.detailRect = { x: px, y: py, w: pw, h: ph };

  // Dim the map background
  ctx.fillStyle = "rgba(10,8,5,0.75)";
  ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

  // Panel background
  drawRoundRect(
    ctx, px, py, pw, ph, 0,
    C_DETAIL_BG,
    C_DETAIL_BORDER,
  );

  // Clip to panel
  ctx.save();
  ctx.beginPath();
  ctx.rect(px, py, pw, ph);
  ctx.clip();

  // ── Scroll momentum ────────────────────────────────────────────────
  const scrollY = appState.scrollY;

  // ── Content ────────────────────────────────────────────────────────
  const innerX = px + 10;
  const innerW = pw - 20;
  let curY = py + 4 - scrollY;

  // ── Zone-themed banner ──────────────────────────────────────────
  const bannerH = 40;
  const bannerGrad = ctx.createLinearGradient(px, curY, px, curY + bannerH);
  bannerGrad.addColorStop(0, hexAlpha(zone.primaryColor, 0.25));
  bannerGrad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = bannerGrad;
  ctx.fillRect(px, curY, pw, bannerH);

  // Icon glyph in banner center
  const bannerGlyph = zoneIconGlyph(zone.icon);
  ctx.font = "18px sans-serif";
  ctx.fillStyle = hexAlpha(zone.primaryColor, 0.35);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(bannerGlyph, px + pw / 2, curY + bannerH / 2);

  // Zone name over banner
  ctx.font = "bold 11px sans-serif";
  ctx.fillStyle = C_TEXT_CREAM;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(zone.name, px + pw / 2, curY + 8);

  // Color stripe under name
  const stripeW = Math.min(innerW, ctx.measureText(zone.name).width + 20);
  ctx.fillStyle = zone.primaryColor;
  ctx.fillRect(px + (pw - stripeW) / 2, curY + 23, stripeW, 1.5);

  curY += bannerH + 4;

  // Type badge + danger indicators
  const badgeColor = typeBadgeColor(zone.type);
  const badgeLabel = typeLabel(zone.type);
  ctx.font = "bold 7px sans-serif";
  const badgeW = ctx.measureText(badgeLabel).width + 10;
  drawRoundRect(
    ctx, innerX, curY, badgeW, 12, 4,
    badgeColor + "40",
    badgeColor + "80",
  );
  ctx.font = "bold 7px sans-serif";
  ctx.fillStyle = badgeColor;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(badgeLabel, innerX + 5, curY + 6);

  // Danger dots
  const dotStartX = innerX + badgeW + 6;
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.arc(dotStartX + i * 9, curY + 6, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = i < zone.dangerLevel ? dangerColor(zone.dangerLevel) : "rgba(100,90,70,0.25)";
    ctx.fill();
  }
  curY += 18;

  // Description — italic, wrapped
  const descLines = wrapText(ctx, zone.description, innerW, "italic 8px sans-serif");
  ctx.font = "italic 8px sans-serif";
  ctx.fillStyle = C_TEXT_TAN;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  for (const line of descLines) {
    ctx.fillText(line, innerX, curY);
    curY += 12;
  }
  curY += 6;

  // Separator
  ctx.fillStyle = "rgba(200,168,120,0.08)";
  ctx.fillRect(innerX, curY, innerW, 1);
  curY += 8;

  // ── Stats ──────────────────────────────────────────────────────────

  // Discovery bar
  drawStatRow(ctx, innerX, curY, innerW, "Discovery", zone.explored, zone.primaryColor);
  curY += 14;

  // Weather
  ctx.font = "8px sans-serif";
  ctx.fillStyle = C_TEXT_LABEL;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(`Weather: ${weatherLabel(zone.defaultWeather)}`, innerX, curY);
  curY += 12;

  // Mood bars
  const moods: Array<[string, number, string]> = [
    ["Warmth",  zone.defaultMood.warmth,  "#e88050"],
    ["Energy",  zone.defaultMood.energy,  "#70c0e0"],
    ["Mystery", zone.defaultMood.mystery, "#9060c0"],
    ["Nature",  zone.defaultMood.nature,  "#60a860"],
  ];
  for (const [label, val, color] of moods) {
    drawStatRow(ctx, innerX, curY, innerW, label, val, color);
    curY += 13;
  }
  curY += 6;

  // Separator
  ctx.fillStyle = "rgba(200,168,120,0.08)";
  ctx.fillRect(innerX, curY, innerW, 1);
  curY += 8;

  // ── Connections ────────────────────────────────────────────────────

  ctx.font = "bold 8px sans-serif";
  ctx.fillStyle = C_TEXT_CREAM;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("Connected to:", innerX, curY);
  curY += 12;

  const connected = getConnectedZones(worldState, zone.id);
  appState.connectionRects = [];

  for (const conn of connected) {
    const btnH = 18;
    drawRoundRect(
      ctx, innerX, curY, innerW, btnH, 4,
      "rgba(200,168,120,0.06)",
      "rgba(200,168,120,0.12)",
    );

    // Small color dot
    ctx.beginPath();
    ctx.arc(innerX + 8, curY + btnH / 2, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = conn.explored > 0 ? conn.primaryColor : C_NODE_UNDISC;
    ctx.fill();

    ctx.font = "8px sans-serif";
    ctx.fillStyle = conn.explored > 0 ? C_TEXT_LABEL : C_TEXT_DIM;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(
      conn.explored > 0 ? conn.name : "???",
      innerX + 16, curY + btnH / 2,
    );

    appState.connectionRects.push({
      zoneId: conn.id,
      rect: { x: px + 10, y: curY, w: innerW, h: btnH },
    });

    curY += btnH + 4;
  }
  curY += 6;

  // Separator
  ctx.fillStyle = "rgba(200,168,120,0.08)";
  ctx.fillRect(innerX, curY, innerW, 1);
  curY += 8;

  // ── Travel Button ──────────────────────────────────────────────────

  const isCurrent = worldState.currentZoneId === zone.id;
  const playerRep = state.player.reputation;
  const travelCheck = canTravel(worldState, zone.id, playerRep);

  const btnH = 24;
  const btnY = curY;

  const isActiveTraveling = worldState.travelState.active;
  const isTravelingToThis = isActiveTraveling && worldState.travelState.toZoneId === zone.id;

  if (isActiveTraveling && isTravelingToThis) {
    // Travel in progress — animated progress bar
    const progress = worldState.travelState.progress;
    const phase = worldState.travelState.phase;
    drawRoundRect(
      ctx, innerX, btnY, innerW, btnH, 6,
      "rgba(232,160,80,0.12)",
      "rgba(232,160,80,0.25)",
    );
    // Progress fill
    const fillW = Math.max(6, (innerW - 4) * progress);
    drawRoundRect(
      ctx, innerX + 2, btnY + 2, fillW, btnH - 4, 4,
      "rgba(232,160,80,0.3)",
    );
    ctx.font = "bold 8px sans-serif";
    ctx.fillStyle = C_TRAVEL_BTN_A;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const phaseLabel = phase === "departing" ? "Departing…" : phase === "arriving" ? "Arriving…" : "Traveling…";
    ctx.fillText(
      `${phaseLabel} ${Math.round(progress * 100)}%`,
      innerX + innerW / 2, btnY + btnH / 2,
    );
    appState.travelBtnRect = null;
  } else if (isActiveTraveling) {
    // Traveling to a DIFFERENT zone — show dimmed
    drawRoundRect(
      ctx, innerX, btnY, innerW, btnH, 6,
      "rgba(200,168,120,0.06)",
      "rgba(200,168,120,0.1)",
    );
    ctx.font = "8px sans-serif";
    ctx.fillStyle = C_TEXT_DIM;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Already traveling…", innerX + innerW / 2, btnY + btnH / 2);
    appState.travelBtnRect = null;
  } else if (isCurrent) {
    // "You are here" badge
    drawRoundRect(
      ctx, innerX, btnY, innerW, btnH, 6,
      "rgba(200,168,120,0.12)",
      "rgba(200,168,120,0.2)",
    );
    ctx.font = "bold 9px sans-serif";
    ctx.fillStyle = C_CURRENT_GLOW;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("You are here", innerX + innerW / 2, btnY + btnH / 2);
    appState.travelBtnRect = null;
  } else if (travelCheck.allowed) {
    // Travel button — amber gradient
    const grad = ctx.createLinearGradient(innerX, btnY, innerX, btnY + btnH);
    grad.addColorStop(0, C_TRAVEL_BTN_A);
    grad.addColorStop(1, C_TRAVEL_BTN_B);
    drawRoundRect(ctx, innerX, btnY, innerW, btnH, 6);
    ctx.fillStyle = grad;
    ctx.fill();

    ctx.font = "bold 9px sans-serif";
    ctx.fillStyle = "#1a1008";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Travel Here", innerX + innerW / 2, btnY + btnH / 2);

    appState.travelBtnRect = { x: px + 10, y: btnY, w: innerW, h: btnH };
  } else {
    // Dimmed button with reason
    drawRoundRect(
      ctx, innerX, btnY, innerW, btnH, 6,
      "rgba(200,168,120,0.08)",
      "rgba(200,168,120,0.15)",
    );
    ctx.font = "8px sans-serif";
    ctx.fillStyle = C_TEXT_DIM;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      travelCheck.reason ?? "Cannot travel here",
      innerX + innerW / 2, btnY + btnH / 2,
    );
    appState.travelBtnRect = null;
  }

  curY += btnH + 12;

  // Update max scroll — curY already has -scrollY baked in (line: curY = py + 12 - scrollY)
  // so undo it with +scrollY to get true content extent
  appState.maxScrollY = Math.max(0, curY + scrollY - py - ph + 12);

  ctx.restore(); // clip

  // ── Back button — always visible outside the clip ──────────────────

  const backH = 20;
  const backY = rect.y + rect.h - backH - 4;
  drawRoundRect(
    ctx,
    px + 4, backY, pw - 8, backH, 4,
    "rgba(30,24,14,0.9)",
    "rgba(200,168,120,0.2)",
  );
  ctx.font = "bold 8px sans-serif";
  ctx.fillStyle = C_TEXT_LABEL;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("Back to Map", px + pw / 2, backY + backH / 2);

  appState.backBtnRect = { x: px + 4, y: backY, w: pw - 8, h: backH };

  // Animated elapsed (purely to prevent "unused" errors if the param is consumed above)
  void elapsed;
}

function drawStatRow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  label: string,
  value: number,
  color: string,
): void {
  ctx.font = "7px sans-serif";
  ctx.fillStyle = C_TEXT_DIM;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x, y + 5);

  const barX = x + 44;
  const barW = w - 44;
  const barH = 4;
  const barY = y + 3;

  // Bar background
  drawRoundRect(ctx, barX, barY, barW, barH, barH / 2, C_PROGRESS_BG);

  // Bar fill
  if (value > 0) {
    drawRoundRect(
      ctx, barX, barY,
      Math.max(barH, barW * Math.min(1, value)),
      barH, barH / 2,
      color,
    );
  }
}

// ─── Return Home Button (Map View) ───────────────────────────────────────────

function drawReturnHomeButton(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  appState: AtlasState,
  worldState: WorldState,
  elapsed: number,
): void {
  const isHome = worldState.currentZoneId === "ember_lane";
  if (isHome) {
    appState.returnHomeRect = null;
    return;
  }

  const isTraveling = worldState.travelState.active;

  // Pill button dimensions — centered horizontally, just below the top edge
  const btnW = Math.min(rect.w - 16, 150);
  const btnH = 22;
  const btnX = rect.x + (rect.w - btnW) / 2;
  const btnY = rect.y + 8;

  ctx.save();

  if (isTraveling) {
    // Dimmed state — travel is already active
    drawRoundRect(
      ctx, btnX, btnY, btnW, btnH, btnH / 2,
      "rgba(180,150,100,0.12)",
      "rgba(180,150,100,0.2)",
    );

    ctx.font = "7px sans-serif";
    ctx.fillStyle = C_TEXT_DIM;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Traveling\u2026", btnX + btnW / 2, btnY + btnH / 2);
    appState.returnHomeRect = null;
  } else {
    // Warm amber/gold pill — gentle pulsing glow
    const glowAlpha = 0.08 + Math.sin(elapsed * 1.5) * 0.04;
    ctx.beginPath();
    const glowR = btnH / 2 + 3;
    ctx.roundRect(btnX - 3, btnY - 3, btnW + 6, btnH + 6, glowR);
    ctx.fillStyle = `rgba(240,200,96,${glowAlpha})`;
    ctx.fill();

    // Button fill — amber gradient
    const grad = ctx.createLinearGradient(btnX, btnY, btnX, btnY + btnH);
    grad.addColorStop(0, "#e8b050");
    grad.addColorStop(1, "#c88838");
    ctx.beginPath();
    ctx.roundRect(btnX, btnY, btnW, btnH, btnH / 2);
    ctx.fillStyle = grad;
    ctx.fill();

    // Subtle border
    ctx.strokeStyle = "rgba(255,220,160,0.35)";
    ctx.lineWidth = 0.8;
    ctx.stroke();

    // House icon — simple triangle roof + rectangle body
    const iconX = btnX + 14;
    const iconY = btnY + btnH / 2;
    const houseColor = "#1a1008";

    // Roof — triangle
    ctx.beginPath();
    ctx.moveTo(iconX, iconY - 5);       // peak
    ctx.lineTo(iconX - 5, iconY - 1);   // bottom-left
    ctx.lineTo(iconX + 5, iconY - 1);   // bottom-right
    ctx.closePath();
    ctx.fillStyle = houseColor;
    ctx.fill();

    // Body — rectangle
    ctx.fillRect(iconX - 3.5, iconY - 1, 7, 5);

    // Door — small rectangle
    ctx.fillStyle = "#e8b050";
    ctx.fillRect(iconX - 1, iconY + 1, 2, 3);

    // Label
    ctx.font = "bold 8px sans-serif";
    ctx.fillStyle = "#1a1008";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Return to Ember Lane", btnX + btnW / 2 + 4, btnY + btnH / 2);

    appState.returnHomeRect = { x: btnX, y: btnY, w: btnW, h: btnH };
  }

  ctx.restore();
}

// ─── Exploring Message ────────────────────────────────────────────────────────

function drawExploringMessage(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  elapsed: number,
): void {
  const alpha = 0.5 + Math.sin(elapsed * 1.2) * 0.2;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = "italic 10px sans-serif";
  ctx.fillStyle = C_TEXT_LABEL;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(
    "Exploring…",
    rect.x + rect.w / 2,
    rect.y + rect.h / 2,
  );
  ctx.restore();
}

// ─── Icon ─────────────────────────────────────────────────────────────────────

function drawAtlasIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  const s = size * 0.36;

  ctx.save();
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.lineWidth = 1.2;
  ctx.lineCap = "round";

  // Outer circle
  ctx.beginPath();
  ctx.arc(cx, cy, s, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.85)";
  ctx.stroke();

  // N/S/E/W lines
  const crosses = [
    { dx: 0, dy: -s * 0.9 },
    { dx: 0, dy:  s * 0.9 },
    { dx:  s * 0.9, dy: 0 },
    { dx: -s * 0.9, dy: 0 },
  ];
  for (let i = 0; i < crosses.length; i++) {
    const { dx, dy } = crosses[i];
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + dx, cy + dy);
    // North (i=0) thicker
    ctx.lineWidth = i === 0 ? 1.8 : 1.0;
    ctx.strokeStyle = i === 0
      ? "rgba(255,255,255,0.95)"
      : "rgba(255,255,255,0.65)";
    ctx.stroke();
  }

  // North arrowhead
  const ax = cx;
  const ay = cy - s * 0.9;
  ctx.beginPath();
  ctx.moveTo(ax, ay - 3);
  ctx.lineTo(ax - 2.5, ay + 2);
  ctx.lineTo(ax + 2.5, ay + 2);
  ctx.closePath();
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.fill();

  // Center dot
  ctx.beginPath();
  ctx.arc(cx, cy, 2, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.fill();

  ctx.restore();
}

// ─── The App ──────────────────────────────────────────────────────────────────

const atlasApp: DeviceApp = {
  id: APP_ID,
  name: APP_NAME,
  iconGradient: ["#3a5080", "#1a3060"],

  // ── Icon ─────────────────────────────────────────────────────────────────

  drawIcon(ctx, cx, cy, size) {
    drawAtlasIcon(ctx, cx, cy, size);
  },

  // ── Content ──────────────────────────────────────────────────────────────

  drawContent(ctx, rect, state, dt) {
    const appState = getAppState(state);
    appState.elapsed += dt;

    const worldState = getWorldState(state);

    // ── Parchment background ─────────────────────────────────────────────
    drawMapBackground(ctx, rect);

    if (!worldState) {
      drawExploringMessage(ctx, rect, appState.elapsed);
      return;
    }

    const zones = worldState.zones;
    const currentZoneId = worldState.currentZoneId;

    // ── Update node positions ────────────────────────────────────────────
    const nodePos: Record<string, { x: number; y: number }> = {};
    for (const zone of zones) {
      nodePos[zone.id] = {
        x: rect.x + zone.mapX * rect.w,
        y: rect.y + zone.mapY * rect.h,
      };
    }
    appState.nodePositions = nodePos;

    if (appState.viewMode === "map") {
      // ── Connection paths ────────────────────────────────────────────
      drawConnectionPaths(ctx, rect, zones, nodePos);

      // ── Active travel path animation ─────────────────────────────────
      const ts = worldState.travelState;
      if (ts.active && ts.fromZoneId && ts.toZoneId) {
        const fromPos = nodePos[ts.fromZoneId];
        const toPos = nodePos[ts.toZoneId];
        if (fromPos && toPos) {
          // Glowing path between origin and destination
          ctx.save();
          ctx.setLineDash([4, 3]);
          ctx.lineDashOffset = -appState.elapsed * 30;
          ctx.strokeStyle = "rgba(232,160,80,0.6)";
          ctx.lineWidth = 2.5;
          ctx.beginPath();
          ctx.moveTo(fromPos.x, fromPos.y);
          ctx.lineTo(toPos.x, toPos.y);
          ctx.stroke();
          ctx.setLineDash([]);

          // Traveling dot along the path
          const px = fromPos.x + (toPos.x - fromPos.x) * ts.progress;
          const py = fromPos.y + (toPos.y - fromPos.y) * ts.progress;
          const dotR = 4 + Math.sin(appState.elapsed * 4) * 1.5;

          // Glow
          ctx.beginPath();
          ctx.arc(px, py, dotR + 3, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(232,160,80,0.15)";
          ctx.fill();
          // Dot
          ctx.beginPath();
          ctx.arc(px, py, dotR, 0, Math.PI * 2);
          ctx.fillStyle = "#f0c860";
          ctx.fill();
          ctx.restore();
        }
      }

      // ── Hover detection ─────────────────────────────────────────────
      appState.hoveredZoneId = null;
      if (appState.pointerX >= 0 && appState.pointerY >= 0) {
        const absX = (state.player.device.appContentRect?.x ?? 0) + appState.pointerX;
        const absY = (state.player.device.appContentRect?.y ?? 0) + appState.pointerY;
        let bestDist = Infinity;
        for (const zone of zones) {
          const np = nodePos[zone.id];
          if (!np) continue;
          const dx = absX - np.x;
          const dy = absY - np.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          const zoneR = zone.id === currentZoneId ? NODE_R_CURRENT
            : zone.explored > 0 ? NODE_R : NODE_R_UNDISC;
          const hitR = zoneR + 6;
          if (d < hitR && d < bestDist) {
            bestDist = d;
            appState.hoveredZoneId = zone.id;
          }
        }
      }

      // ── Zone nodes ──────────────────────────────────────────────────
      for (const zone of zones) {
        const { x: nx, y: ny } = nodePos[zone.id];
        drawZoneNode(
          ctx, zone, nx, ny,
          zone.id === currentZoneId,
          zone.id === appState.hoveredZoneId,
          appState.elapsed,
        );
      }

      // ── Hover tooltip ──────────────────────────────────────────────
      if (appState.hoveredZoneId) {
        const hz = getZone(worldState, appState.hoveredZoneId);
        if (hz) {
          const hp = nodePos[hz.id];
          if (hp) {
            if (hz.explored > 0) {
              drawHoverTooltip(ctx, hz, hp.x, hp.y, rect, worldState.currentZoneId === hz.id);
            } else {
              drawUndiscoveredTooltip(ctx, hz, hp.x, hp.y, rect);
            }
          }
        }
      }

      // ── Legend (top-left) ────────────────────────────────────────────
      drawLegend(ctx, rect.x + 4, rect.y + 4);

      // ── Compass rose (bottom-right) ──────────────────────────────────
      drawCompassRose(
        ctx,
        rect.x + rect.w - 22,
        rect.y + rect.h - 22,
      );

      // ── Return Home button (if not already home) ────────────────────
      drawReturnHomeButton(ctx, rect, appState, worldState, appState.elapsed);

    } else if (appState.viewMode === "detail" && appState.selectedZoneId) {
      // ── Detail panel ─────────────────────────────────────────────────
      const selectedZone = getZone(worldState, appState.selectedZoneId);

      if (selectedZone) {
        // Momentum scroll
        if (Math.abs(appState.scrollVelocity) > 0.5) {
          appState.scrollY += appState.scrollVelocity * dt;
          appState.scrollVelocity *= Math.exp(-SCROLL_FRICTION * dt);
        } else {
          appState.scrollVelocity = 0;
        }
        // Clamp scroll
        appState.scrollY = Math.max(0, Math.min(appState.scrollY, appState.maxScrollY));

        drawDetailPanel(
          ctx, rect, selectedZone,
          appState, worldState, state,
          appState.elapsed,
        );
      } else {
        // Zone not found — fall back to map
        appState.viewMode = "map";
        appState.selectedZoneId = null;
      }
    }
  },

  // ── Tap ──────────────────────────────────────────────────────────────────

  handleTap(localX, localY, state) {
    const appState = getAppState(state);
    const worldState = getWorldState(state);
    if (!worldState) return false;

    const contentRect = state.player.device.appContentRect;
    if (!contentRect) return false;

    // Convert local → absolute screen coords (localX/Y relative to content rect)
    const absX = contentRect.x + localX;
    const absY = contentRect.y + localY;

    if (appState.viewMode === "detail") {
      // ── Back button ────────────────────────────────────────────────
      if (appState.backBtnRect) {
        const b = appState.backBtnRect;
        if (absX >= b.x && absX <= b.x + b.w && absY >= b.y && absY <= b.y + b.h) {
          appState.viewMode = "map";
          appState.selectedZoneId = null;
          appState.scrollY = 0;
          appState.scrollVelocity = 0;
          return true;
        }
      }

      // ── Travel button ───────────────────────────────────────────────
      if (appState.travelBtnRect && appState.selectedZoneId && !worldState.travelState.active) {
        const b = appState.travelBtnRect;
        if (absX >= b.x && absX <= b.x + b.w && absY >= b.y && absY <= b.y + b.h) {
          const check = canTravel(worldState, appState.selectedZoneId, state.player.reputation);
          if (check.allowed) {
            startTravel(worldState, appState.selectedZoneId);
            // Reset Atlas UI state so detail panel doesn't show stale data on arrival
            appState.viewMode = "map";
            appState.selectedZoneId = null;
            appState.scrollY = 0;
            appState.scrollVelocity = 0;
          }
          return true;
        }
      }

      // ── Connection zone taps ────────────────────────────────────────
      for (const { zoneId, rect: r } of appState.connectionRects) {
        if (absX >= r.x && absX <= r.x + r.w && absY >= r.y && absY <= r.y + r.h) {
          appState.selectedZoneId = zoneId;
          appState.scrollY = 0;
          appState.scrollVelocity = 0;
          return true;
        }
      }

      // ── Tap outside panel → back to map ────────────────────────────
      const dp = appState.detailRect;
      if (dp && (absX < dp.x || absX > dp.x + dp.w)) {
        appState.viewMode = "map";
        appState.selectedZoneId = null;
        appState.scrollY = 0;
        appState.scrollVelocity = 0;
        return true;
      }

      return false;
    }

    // ── Map view: Return Home button ─────────────────────────────────────
    if (appState.returnHomeRect && !worldState.travelState.active) {
      const rh = appState.returnHomeRect;
      if (absX >= rh.x && absX <= rh.x + rh.w && absY >= rh.y && absY <= rh.y + rh.h) {
        const check = canTravel(worldState, "ember_lane", state.player.reputation);
        if (check.allowed) {
          startTravel(worldState, "ember_lane");
          appState.viewMode = "map";
          appState.selectedZoneId = null;
          appState.scrollY = 0;
          appState.scrollVelocity = 0;
          appState.returnHomeRect = null;
        }
        return true;
      }
    }

    // ── Map view: tap on zone node ──────────────────────────────────────
    for (const zone of worldState.zones) {
      const np = appState.nodePositions[zone.id];
      if (!np) continue;

      // Hit radius slightly larger than visual for finger-friendliness
      const hitR = NODE_RADIUS_BIG + 6;
      const dx = absX - np.x;
      const dy = absY - np.y;
      if (dx * dx + dy * dy <= hitR * hitR) {
        appState.selectedZoneId = zone.id;
        appState.viewMode = "detail";
        appState.scrollY = 0;
        appState.scrollVelocity = 0;
        return true;
      }
    }

    return false;
  },

  // ── Drag scroll ──────────────────────────────────────────────────────────

  handleDrag(dy, state) {
    const appState = getAppState(state);
    if (appState.viewMode !== "detail") return;
    appState.scrollY = Math.max(
      0,
      Math.min(appState.scrollY + dy, appState.maxScrollY),
    );
    appState.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const appState = getAppState(state);
    if (appState.viewMode !== "detail") return;
    appState.scrollVelocity = velocityY;
  },

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  onBack(state) {
    const appState = getAppState(state);
    if (appState.viewMode === "detail") {
      appState.viewMode = "map";
      appState.selectedZoneId = null;
      appState.scrollY = 0;
      appState.scrollVelocity = 0;
      return true;
    }
    return false;
  },

  onOpen(state) {
    const appState = getAppState(state);
    appState.scrollVelocity = 0;
    appState.viewMode = "map";
    appState.selectedZoneId = null;
    appState.scrollY = 0;
  },

  // ── Pointer tracking for hover effects ──────────────────────────────────

  handlePointerMove(x, y, state) {
    const appState = getAppState(state);
    appState.pointerX = x;
    appState.pointerY = y;
  },
};

// ─── Self-Register ────────────────────────────────────────────────────────────

registerApp(atlasApp);
